import { Box, Card, Container, Flex, Text, useMediaQuery } from '@chakra-ui/react'
import { useGetRegisterQueryQuery } from '@core/app/api/authApi'
import { useGetThemeQuery } from '@core/app/api/globalApi'
import {
    addStepCount,
    currentStep,
    setAllStepsCount,
    setAuthServiceErrorMessage,
    setDateOfBirth,
    setEmail,
    setExternalStep,
    setFirstName,
    setHaveDepartments,
    setIsExternalProcess,
    setLastName,
    setMobilePhoneNumber,
    setMobilePhonePrefix,
    setRepeatEmail,
    setServiceId,
    setSettings,
} from '@core/app/slices/auth'
import { AuthApi } from '@core/app/types/authApiType'
import bg from '@core/assets/images/register_bg.jpg'
import { Header } from '@core/components/Header/Header'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import useViewportHeight from '@core/utils/helpers/viewportHeight'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'
import { EmailVerificationStep } from '@page/signup/components/registerFlow/emailVerification/emailVerification'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CreateProfileStep } from './components/registerFlow/createProfile/createProfile'
import { DepartmentsChooseStep } from './components/registerFlow/departmentsChoose/departmentsChoose'
import { PersonalDataExtendedStep } from './components/registerFlow/personalDataExtended/personalDataExtanded'
import { PhoneVerificationStep } from './components/registerFlow/phoneVerification/phoneVerification'

export const SignupPage = () => {
    const dispatch = useAppDispatch()
    const step = useAppSelector(currentStep)
    const { data: registerParams } = useGetRegisterQueryQuery({
        code: localStorage.getItem('service_code_auth') ? `?code=${localStorage.getItem('service_code_auth')}` : '',
    })
    const { appTheme, isExpired, updateTheme } = useAppTheme()
    const { data: theme } = useGetThemeQuery({}, { skip: isExpired })

    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const viewportHeight = useViewportHeight()
    const [isDesktop] = useMediaQuery('min-width: 1230px')

    const [bgImage, setBgImage] = useState<string>('')
    const [bgColor, setBgColor] = useState<string>('')
    const [gender, setGender] = useState<'0' | '1' | '2' | '3' | '4'>('0')
    const [linkedinUrl, setLinkedinUrl] = useState<string | null>(null)

    const onSetGender = (value: '0' | '1' | '2' | '3' | '4') => {
        setGender(value)
    }

    const isHasMandatoryOrVisible = Object.values(registerParams?.fields || {}).some(
        ({ visible, mandatory }) => visible || mandatory
    )

    const calculateSteps = (params: AuthApi) => {
        let defaultStepsCount = 2

        if (Object.values(params?.departments || {}).length > 0) defaultStepsCount++

        // if (params?.settings?.validateMobile && registerParams?.requiredFields?.mobilePhone) defaultStepsCount++
        if (params?.settings?.validateMobile && registerParams?.fields?.mobilePhone.visible) defaultStepsCount++
        if (params?.settings?.verifyEmailBeforeRegistration) defaultStepsCount++

        return defaultStepsCount
    }

    useEffect(() => {
        removeLocalStorageItem('feedbackTop')
    }, [])

    useEffect(() => {
        const service_code = searchParams.get('code')

        if (service_code) {
            localStorage.setItem('service_code_auth', service_code)
            navigate(
                {
                    search: '',
                },
                { replace: true }
            )
        }
    }, [searchParams])

    useEffect(() => {
        if (registerParams) {
            dispatch(setAllStepsCount(calculateSteps(registerParams)))
            dispatch(setSettings(registerParams?.settings))
            dispatch(setHaveDepartments(Object.keys(registerParams.departments || {}).length > 0))
            if (registerParams.isExternalProcess && registerParams.externalAuth?.isValid) {
                let externalStep = registerParams.externalAuth?.profile?.email
                    ? 'personalDataExtanded'
                    : 'createProfile'
                if (externalStep === 'personalDataExtanded') {
                    dispatch(addStepCount())
                }
                dispatch(setIsExternalProcess(registerParams.isExternalProcess))
                dispatch(setFirstName(registerParams.externalAuth?.profile?.firstName))
                dispatch(setLastName(registerParams.externalAuth?.profile?.lastName))
                dispatch(setEmail(registerParams.externalAuth?.profile?.email))
                dispatch(setExternalStep(externalStep))
                dispatch(setRepeatEmail(registerParams.externalAuth?.profile?.email))
                dispatch(setDateOfBirth(registerParams.externalAuth?.profile?.birthday))
                dispatch(setMobilePhonePrefix(registerParams.externalAuth?.profile?.mobilePhone?.prefix))
                dispatch(setMobilePhoneNumber(registerParams.externalAuth?.profile?.mobilePhone?.number))
                dispatch(setServiceId(registerParams.externalAuth?.code))
            } else if (registerParams.isExternalProcess && !registerParams.externalAuth?.isValid) {
                dispatch(setAuthServiceErrorMessage(registerParams.externalAuth?.message))
            }
        }
    }, [registerParams])

    useEffect(() => {
        if (!appTheme) return
        let newSettings = appTheme?.newSettings?.general
        if (newSettings) {
            if (newSettings.BackgroundUrl) {
                setBgImage(newSettings.BackgroundUrl)
            } else {
                setBgImage(bg)
            }
        } else {
            setBgImage(bg)
        }
    }, [appTheme])

    useEffect(() => {
        if (theme) {
            updateTheme(theme)
        }
    }, [theme])

    return (
        <Flex
            minH={'100vh'}
            bgImage={bgImage && bgImage}
            bgColor={bgColor && bgColor}
            backgroundSize={'cover'}
            flexDirection={'column'}
        >
            <Header />
            <Box as="section" flexGrow={1} display={'flex'} alignItems={'center'}>
                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }} h={'full'}>
                    <Flex justifyContent={'end'} alignItems={'center'} h={'full'}>
                        <Card
                            w={{ base: 'full', xl: '440px' }}
                            bg={'text.white'}
                            py={{ base: '32px', xl: '44px' }}
                            px={{ base: '28px', xl: '48px' }}
                            my={{ base: 0, xl: '32px' }}
                            mx={{ base: 0, xl: '32px' }}
                        >
                            {step === 'createProfile' && (
                                <CreateProfileStep
                                    isFacebook={registerParams?.settings?.facebook}
                                    isMicrosoft={registerParams?.settings?.microsoft}
                                    isGoogle={registerParams?.settings?.google}
                                    isLinkedin={registerParams?.settings?.linkedin}
                                    isVipps={registerParams?.settings?.vipps}
                                    isNoBankId={registerParams?.settings?.nobankid}
                                    isSeBankId={registerParams?.settings?.sebankid}
                                    registerSettings={registerParams}
                                />
                            )}
                            {step === 'emailVerification' &&
                                registerParams?.settings?.verifyEmailBeforeRegistration && <EmailVerificationStep />}
                            {/*{(step === 'personalDataExtanded' && isHasMandatoryOrVisible) && (*/}
                            {step === 'personalDataExtanded' && (
                                <PersonalDataExtendedStep
                                    fields={registerParams?.fields}
                                    // requiredSettings={registerParams?.settings}
                                    gender={gender}
                                    linkedinUrl={linkedinUrl}
                                    onChangeLinkedinUrl={setLinkedinUrl}
                                    onChangeGender={onSetGender}
                                />
                            )}
                            {step === 'departments' && registerParams?.departments && (
                                <DepartmentsChooseStep
                                    departments={registerParams?.departments}
                                    fields={registerParams?.fields}
                                    gender={gender}
                                    linkedinUrl={linkedinUrl}
                                />
                            )}
                            {step === 'phoneVerification' && registerParams?.settings?.validateMobile && (
                                <PhoneVerificationStep
                                    gender={gender}
                                    linkedinUrl={linkedinUrl}
                                    fields={registerParams?.fields}
                                />
                            )}
                        </Card>
                    </Flex>
                </Container>
            </Box>
            <Box as={'footer'} py={{ base: 8, xl: 6 }} bg={{ base: 'rgba(0, 0, 0, 0.5)' }}>
                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }}>
                    <Flex justifyContent={{ base: 'center', xl: 'end' }}>
                        {appTheme?.customerPortalLink && (
                            <Box
                                as={'a'}
                                href={appTheme.customerPortalLink}
                                fontSize={'xs'}
                                color={'text.white'}
                                cursor={'pointer'}
                                _hover={{
                                    textDecoration: 'underline',
                                }}
                                mr={4}
                            >
                                {t('7948')}
                            </Box>
                        )}

                        <Text color={'text.white'} fontSize={'xs'}>
                            {t('6587')} -{' '}
                            <Box as={'a'} href={'/privacy-policy'} color={'text.link'} target={'_blank'}>
                                {t('5034')}
                            </Box>
                        </Text>
                    </Flex>
                </Container>
            </Box>
        </Flex>
    )
}
